import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, BookOpen, Download, Calendar, Loader2 } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklyPromptsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/weekly-prompts`).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/bonus/download-weekly-prompts`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "Weekly-Journaling-Prompts-ThryvLife.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloading(false); }
  };

  const weekColors = ["bg-[#4FB3D4]/10 border-[#4FB3D4]/20", "bg-[#A69BE0]/10 border-[#A69BE0]/20", "bg-[#FFB7B2]/10 border-[#FFB7B2]/20", "bg-green-50 border-green-200"];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="weekly-prompts-section">
        <div className="container mx-auto max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-6 text-sm"><ArrowLeft className="h-4 w-4" /> Back</Link>

          <div className="text-center mb-8">
            <BookOpen className="h-10 w-10 text-[#A69BE0] mx-auto mb-4" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="prompts-title">Weekly Guided Journaling Prompts</h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">4 weeks of daily prompts to guide your recovery. New prompts rotate each week. Print them, screenshot them, or journal in your own notebook.</p>
          </div>

          <div className="text-center mb-8">
            <Button onClick={handleDownload} disabled={downloading} className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-semibold" data-testid="download-prompts-btn">
              <Download className="mr-2 h-4 w-4" /> {downloading ? "Downloading..." : "Download All 4 Weeks (PDF)"}
            </Button>
          </div>

          {loading ? <div className="text-center"><Loader2 className="h-8 w-8 animate-spin text-[#4FB3D4] mx-auto" /></div> : (
            <>
              {data?.current_week && (
                <Card className="bg-gradient-to-br from-[#4FB3D4]/5 to-[#DCD6F7]/10 rounded-2xl border-2 border-[#4FB3D4]/20 mb-8" data-testid="current-week">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="h-5 w-5 text-[#4FB3D4]" />
                      <h2 className="font-heading text-lg font-semibold text-[#1A2A3A]">This Week: {data.current_week.theme}</h2>
                    </div>
                    <ul className="space-y-2">
                      {data.current_week.prompts.map((p, i) => (
                        <li key={i} className="text-sm text-[#1A2A3A]/70 bg-white rounded-lg px-4 py-3 border border-[#E5E7EB]/50">{p}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <h2 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-4">All 4 Weeks</h2>
              <div className="space-y-4">
                {data?.all_weeks?.map((week, i) => (
                  <Card key={i} className={`rounded-xl border ${weekColors[i]} overflow-hidden`} data-testid={`week-${i}`}>
                    <CardContent className="p-5">
                      <h3 className="font-heading text-base font-semibold text-[#1A2A3A] mb-3">Week {week.week}: {week.theme}</h3>
                      <ul className="space-y-1.5">
                        {week.prompts.map((p, j) => (
                          <li key={j} className="text-sm text-[#1A2A3A]/60">{p}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      <footer className="py-8 px-6 bg-[#1A2A3A] text-white text-center">
        <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB)</p>
      </footer>
    </div>
  );
};

export default WeeklyPromptsPage;
